import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Session } from '@/types/models';

/**
 * Auth state.
 *
 * Only the TOKEN and the active outlet persist to localStorage (ADR-004).
 * The session payload itself is deliberately not persisted: it carries
 * capabilities and subscription state, and a stale copy would show a user
 * buttons they no longer have — or, worse, hide ones they do. It is refetched
 * from GET /me on every mount, which is one request and always correct.
 *
 * Impersonation state lives here too so the banner can render above
 * everything without prop-drilling.
 */

type AuthState = {
  token: string | null;
  activeOutletId: number | null;
  session: Session | null;
  isImpersonating: boolean;

  setToken: (token: string | null) => void;
  setSession: (session: Session | null) => void;
  setActiveOutlet: (outletId: number | null) => void;
  setImpersonating: (value: boolean) => void;
  clear: () => void;
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      activeOutletId: null,
      session: null,
      isImpersonating: false,

      setToken: (token) => set({ token }),

      setSession: (session) => {
        // Default the active outlet on first load: the user's home outlet,
        // then the tenant default, then whatever exists. Without this the
        // X-Outlet-Id header is absent and the POS has no shop to sell from.
        const current = get().activeOutletId;
        const outlets = session?.outlets ?? [];

        const resolved =
          current && outlets.some((o) => o.id === current)
            ? current
            : (session?.user.home_outlet_id ??
              outlets.find((o) => o.is_default)?.id ??
              outlets[0]?.id ??
              null);

        set({ session, activeOutletId: resolved });
      },

      setActiveOutlet: (activeOutletId) => set({ activeOutletId }),
      setImpersonating: (isImpersonating) => set({ isImpersonating }),

      clear: () =>
        set({ token: null, session: null, activeOutletId: null, isImpersonating: false }),
    }),
    {
      name: 'voltra.auth',
      partialize: (state) => ({
        token: state.token,
        activeOutletId: state.activeOutletId,
        isImpersonating: state.isImpersonating,
      }),
    },
  ),
);

/* --- Selectors. Components subscribe to the narrowest slice they need, so a
       session refetch does not re-render the whole tree. --- */

export const useToken = () => useAuthStore((s) => s.token);
export const useSession = () => useAuthStore((s) => s.session);
export const useCurrentUser = () => useAuthStore((s) => s.session?.user ?? null);
export const useTenant = () => useAuthStore((s) => s.session?.tenant ?? null);
export const useSubscription = () => useAuthStore((s) => s.session?.subscription ?? null);
export const useActiveOutletId = () => useAuthStore((s) => s.activeOutletId);

export const useActiveOutlet = () =>
  useAuthStore((s) => s.session?.outlets.find((o) => o.id === s.activeOutletId) ?? null);
